import React, { useContext } from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import RoomContext from "../../context/RoomContext";
import { useIntl, FormattedMessage } from "react-intl";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CustomSelect from "../Common/CustomSelect";
import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { setSoundNotificationsDisabled } from "../../actions/settingsActions";
import { setDisplayMode, setSettingsOpen } from "../../actions/roomActions";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) =>
  createStyles({
    setting: {
      paddingTop: theme.spacing(1),
    },
    dialogTitle: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      padding: theme.spacing(4),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(2),
      },
    },
    dialogActions: {
      justifyContent: "flex-end",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(2),
      },
    },
    button: {
      width: "auto",
      marginTop: 0,
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(2),
      top: theme.spacing(2),
      color: theme.palette.type === "dark" ? "#fff" : "#181C1E",
      border: "1px solid #EAEDF0",
      [theme.breakpoints.down("xs")]: {
        top: theme.spacing(1),
      },
    },
    settingsSection: {
      marginBottom: theme.spacing(1),
    },
  })
);

const audioOutputSupportedBrowsers = ["Chrome", "Opera"];

const Settings = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { roomClient } = useContext(RoomContext);
  const me = useSelector((state) => state.me);
  const room = useSelector((state) => state.room);
  const settings = useSelector((state) => state.settings);
  const browser = me.browser;

  const modes = [
    {
      value: "democratic",
      label: intl.formatMessage({
        id: "label.democratic",
        defaultMessage: "Democratic view",
      }),
    },
    {
      value: "filmstrip",
      label: intl.formatMessage({
        id: "label.filmstrip",
        defaultMessage: "Filmstrip view",
      }),
    },
  ];

  const resolutions = [
    {
      value: "low",
      label: intl.formatMessage({
        id: "label.low",
        defaultMessage: "Low",
      }),
    },
    {
      value: "medium",
      label: intl.formatMessage({
        id: "label.medium",
        defaultMessage: "Medium",
      }),
    },
    {
      value: "high",
      label: intl.formatMessage({
        id: "label.high",
        defaultMessage: "High (HD)",
      }),
    },
    {
      value: "veryhigh",
      label: intl.formatMessage({
        id: "label.veryHigh",
        defaultMessage: "Very high (FHD)",
      }),
    },
    {
      value: "ultra",
      label: intl.formatMessage({
        id: "label.ultra",
        defaultMessage: "Ultra (UHD)",
      }),
    },
  ];

  let webcams;

  if (me.webcamDevices) webcams = Object.values(me.webcamDevices);
  else webcams = [];

  let audioDevices;

  if (me.audioDevices) audioDevices = Object.values(me.audioDevices);
  else audioDevices = [];

  let audioOutputDevices;

  if (me.audioOutputDevices)
    audioOutputDevices = Object.values(me.audioOutputDevices);
  else audioOutputDevices = [];

  return (
    <Dialog
      open={room.settingsOpen}
      onClose={() => setSettingsOpen({ settingsOpen: false })}
    >
      <DialogTitle className={classes.dialogTitle} id="form-dialog-title">
        <FormattedMessage id="settings.settings" defaultMessage="Settings" />
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={() => {
            dispatch(setSettingsOpen({ settingsOpen: false }));
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className={classes.settingsSection}>
          <Typography variant="h5">
            <FormattedMessage
              id="settings.audioSettings"
              defaultMessage="Audio"
            />
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <CustomSelect
                text={
                  audioDevices.length > 0
                    ? intl.formatMessage({
                        id: "settings.selectAudio",
                        defaultMessage: "Select audio device",
                      })
                    : intl.formatMessage({
                        id: "settings.cantSelectAudio",
                        defaultMessage: "Unable to select audio device",
                      })
                }
                value={
                  audioDevices.length > 0
                    ? settings.selectedAudioDevice || ""
                    : ""
                }
                onChange={(event) => {
                  if (event.target.value)
                    roomClient.changeAudioDevice(event.target.value);
                }}
                disabled={audioDevices.length === 0 || me.audioInProgress}
              >
                {audioDevices.map((audio, index) => {
                  return (
                    <MenuItem key={index} value={audio.deviceId}>
                      {audio.label}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
            </Grid>
            <Grid item xs={12} sm={6}>
              {audioOutputSupportedBrowsers &&
                audioOutputSupportedBrowsers.includes(browser.name) && (
                  <CustomSelect
                    text={
                      audioOutputDevices.length > 0
                        ? intl.formatMessage({
                            id: "settings.selectAudio.output",
                            defaultMessage: "Select audio output device",
                          })
                        : intl.formatMessage({
                            id: "settings.cantSelectAudio.output",
                            defaultMessage:
                              "Unable to select audio output device",
                          })
                    }
                    value={
                      audioOutputDevices.length > 0
                        ? settings.selectedAudioOutputDevice || ""
                        : ""
                    }
                    onChange={(event) => {
                      if (event.target.value)
                        roomClient.changeAudioOutputDevice(event.target.value);
                    }}
                    displayEmpty
                    autoWidth
                    disabled={
                      audioOutputDevices.length === 0 ||
                      me.audioOutputInProgress
                    }
                  >
                    {audioOutputDevices.map((audioOutput, index) => {
                      return (
                        <MenuItem key={index} value={audioOutput.deviceId}>
                          {audioOutput.label}
                        </MenuItem>
                      );
                    })}
                  </CustomSelect>
                )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormHelperText>
                {intl.formatMessage({
                  id: "settings.disableSoundNotifications",
                  defaultMessage: "Disable sound notifications",
                })}
              </FormHelperText>
              <FormControlLabel
                className={classes.setting}
                control={
                  <Checkbox
                    checked={settings.soundNotificationsDisabled}
                    onChange={() =>
                      dispatch(
                        setSoundNotificationsDisabled(
                          !settings.soundNotificationsDisabled
                        )
                      )
                    }
                    value="soundNotificationsDisabled"
                  />
                }
                label={intl.formatMessage({
                  id: "settings.disableSoundNotifications",
                  defaultMessage: "Disable sound notifications",
                })}
              />
            </Grid>
          </Grid>
        </div>
        <div className={classes.settingsSection}>
          <Typography variant="h5">
            <FormattedMessage
              id="settings.videoSettings"
              defaultMessage="Video"
            />
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <CustomSelect
                text={
                  webcams.length > 0
                    ? intl.formatMessage({
                        id: "settings.selectCamera",
                        defaultMessage: "Select video device",
                      })
                    : intl.formatMessage({
                        id: "settings.cantSelectCamera",
                        defaultMessage: "Unable to select video device",
                      })
                }
                value={webcams.length > 0 ? settings.selectedWebcam || "" : ""}
                onChange={(event) => {
                  if (event.target.value)
                    roomClient.changeWebcam(event.target.value);
                }}
                disabled={webcams.length === 0 || me.webcamInProgress}
              >
                {webcams.map((webcam, index) => {
                  return (
                    <MenuItem key={index} value={webcam.deviceId}>
                      {webcam.label}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
            </Grid>
            {/*            <Grid item xs={12} sm={6}>
              <CustomSelect
                text={
                  <FormattedMessage
                    id="settings.resolution"
                    defaultMessage="Select your video resolution"
                  />
                }
                value={settings.resolution || ""}
                onChange={(event) => {
                  if (event.target.value)
                    roomClient.changeVideoResolution(event.target.value);
                }}
                autoWidth
              >
                {resolutions.map((resolution, index) => {
                  return (
                    <MenuItem key={index} value={resolution.value}>
                      {resolution.label}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
            </Grid>*/}
          </Grid>
        </div>
        <div className={classes.settingsSection}>
          <Typography variant="h5">
            <FormattedMessage
              id="settings.otherSettings"
              defaultMessage="Others"
            />
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12}>
              <CustomSelect
                text={
                  <FormattedMessage
                    id="settings.selectRoomLayout"
                    defaultMessage="Select room layout"
                  />
                }
                value={room.mode || ""}
                onChange={(event) => {
                  if (event.target.value) {
                    dispatch(setDisplayMode(event.target.value));
                  }
                }}
                autoWidth
              >
                {modes.map((mode, index) => {
                  return (
                    <MenuItem key={index} value={mode.value}>
                      {mode.label}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
            </Grid>
            {/*            <Grid item xs={12} sm={12}>
              <FormHelperText>
                {intl.formatMessage({
                  id: "settings.advancedMode",
                  defaultMessage: "Advanced mode",
                })}
              </FormHelperText>
              <FormControlLabel
                className={classes.setting}
                control={
                  <Checkbox
                    checked={settings.advancedMode}
                    onChange={() => dispatch(toggleAdvancedMode())}
                    value="advancedMode"
                  />
                }
                label={intl.formatMessage({
                  id: "settings.advancedMode",
                  defaultMessage: "Advanced mode",
                })}
              />
            </Grid>*/}
            <Grid item xs={12} sm={12}>
              {settings.advancedMode && (
                <CustomSelect
                  text={
                    <FormattedMessage
                      id="settings.lastn"
                      defaultMessage="Number of visible videos"
                    />
                  }
                  value={settings.lastN || ""}
                  onChange={(event) => {
                    if (event.target.value)
                      roomClient.changeMaxSpotlights(event.target.value);
                  }}
                  autoWidth
                >
                  {[
                    1,
                    2,
                    3,
                    4,
                    5,
                    6,
                    7,
                    8,
                    9,
                    10,
                    11,
                    12,
                    13,
                    14,
                    15,
                    16,
                    17,
                    18,
                    19,
                    20,
                  ].map((lastN) => {
                    return (
                      <MenuItem key={lastN} value={lastN}>
                        {lastN}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              )}
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.button}
          onClick={() => dispatch(setSettingsOpen({ settingsOpen: false }))}
          variant="contained"
          color="secondary"
        >
          <FormattedMessage id="label.done" defaultMessage="Done" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Settings;
